import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiUpload } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";

export default function EditMemberPopup({
  title, // Title of the popup
  isOpen, // Boolean to control popup visibility
  onClose, // Function to close the popup
  fetchMembers, // Function to fetch council members
  updateMember, // Function to update a council member
  data, // Data of the member being edited
}) {
  const dispatch = useDispatch();
  const [editMessage, setEditMessage] = useState({
    id: "",
    name: "",
    designation: "",
    biography: "",
    file: null, // New file object
    imagePreview: null, // For previewing the new image
    image_url: "", // URL to the current image
  });

  useEffect(() => {
    if (data) {
      setEditMessage({
        id: data.id || "",
        name: data.name || "",
        designation: data.designation || "",
        biography: data.biography || "",
        image_url: data.image_url || "",
        file: null,
        imagePreview: null,
      });
    } else {
      setEditMessage({
        id: "",
        name: "",
        designation: "",
        biography: "",
        image_url: "",
        file: null,
        imagePreview: null,
      });
    }
  }, [data]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditMessage((prevState) => ({
          ...prevState,
          file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setEditMessage((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const { id, name, designation, biography, file, image_url } = editMessage;

    // Validate required fields
    if (!name || !designation || !biography.trim()) {
      alert("Please fill out all required fields.");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("name", name);
    formDataToSend.append("designation", designation);
    formDataToSend.append("biography", biography);
    formDataToSend.append("file", file);

    try {
      await dispatch(updateMember(id, formDataToSend)); // Call update function
      alert("Member updated successfully!");
      onClose(); // Close the popup
      dispatch(fetchMembers()); // Refresh member list
    } catch (error) {
      console.error("Error updating member:", error);
      alert("Failed to update member. Please try again.");
    }
  };

  if (!isOpen) return null;

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["clean"],
    ],
  };

  const formats = ["font", "header", "bold", "italic", "underline", "align"];

  return (
    <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
      <div className="w-[1100px] h-[600px] p-8 bg-white rounded-lg shadow-lg overflow-y-auto relative">
        <div className="flex items-center justify-between mb-6">
          <h2 className="mt-10 text-2xl font-semibold text-gray-700">{title}</h2>
          <button
            type="button"
            className="text-gray-500 border rounded-md shadow-md hover:text-gray-700"
            onClick={onClose}
          >
            <IoClose className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="name">
                Name
              </label>
              <input
                id="name"
                type="text"
                value={editMessage.name }
                onChange={(e) => handleInputChange("name", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="designation">
                Designation
              </label>
              <input
                id="designation"
                type="text"
                value={editMessage.designation || ""}
                onChange={(e) => handleInputChange("designation", e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="flex flex-col">
              <div className="flex flex-col items-center justify-center w-full h-[225px] p-5 border border-gray-300 border-dashed rounded-md relative">
                <input
                  id="fileInput"
                  type="file"
                  name="file"
                  accept=".jpg, .png,.webp, .jpeg"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setEditMessage({ ...editMessage, file, imagePreview: reader.result });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {(editMessage.imagePreview || editMessage.image_url) ? (
                  <div className="relative w-full h-full">
                    <img
                      src={editMessage.imagePreview || editMessage.image_url}
                      alt="Preview"
                      className="object-contain w-full h-full rounded-md opacity-80"
                    />
                    <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full gap-2">
                      <button
                        type="button"
                        className="px-4 py-2 bg-white border rounded-md text-primary-color border-primary-color"
                        onClick={() => document.getElementById('fileInput').click()} // Trigger input click
                      >
                        Browse Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <FiUpload className="w-10 h-10 p-2 mb-3 text-gray-400 bg-gray-100 border-none rounded-md" />
                    <p className="text-black">Drag and drop image here</p>
                    <p className="text-gray-400">or</p>
                    <button
                      type="button"
                      className="px-4 py-2 mt-2 text-[#92288C] font-semibold bg-[#92288C] bg-opacity-10 rounded-md"
                      onClick={() => document.getElementById('fileInput').click()} // Trigger input click
                    >
                      Browse Image
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="col-span-2">
              <label className="block mb-1 text-sm font-medium text-gray-600" htmlFor="biography">
                Biography
              </label>
              <ReactQuill
                id="biography"
                value={editMessage.biography || ""}
                onChange={(value) => handleInputChange("biography", value)}
                theme="snow"
                modules={modules}
                formats={formats}
                className="h-[180px]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              className="px-6 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              onClick={onClose}
            >
              Back
            </button>
            <button
              type="submit"
              className="px-6 py-2 text-white bg-[#92288C] rounded-md hover:bg-[#6C0B67]"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
